/**
 * @lines98/client/lib/i18n/boot-locale — оркестрация активного языка: boot-резолв,
 * применение, персист, sync-при-логине (ADR-0021 D-3/D-4/D-4a, T-I18N-3).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: «применить язык» = подгрузить каталог (i18n runtime) +
 * выставить store-locale (реактив без перезагрузки) + персистнуть (гость localStorage,
 * залогиненный — сервер). Это поведение общее для трёх call-sites (boot, рейл-выбор,
 * sync-при-логине) и НЕ должно дублироваться по ним — вынесено сюда как переиспользуемая
 * ветка (модель ствол→ветки, owner-directive 2026-05-30). app.ts / nav-rail монтируют
 * эти функции, не повторяют логику резолва/персиста.
 *
 * РАЗВЕДЕНИЕ С SEO-ДЕФОЛТОМ (D-3): этот модуль ставит язык ЖИВОГО юзера (store +
 * каталог). SEO-дефолт `en` (canonical/x-default краулера) — отдельный слой (SSG /
 * server HTML-shell), резолвер его НЕ трогает.
 *
 * SCOPE: `bootInitialLocale` (резолв первого захода + применение, без серверной
 *   записи), `applyLocale` (загрузить каталог + store + персист), `persistLocale`
 *   (гость localStorage / залогиненный сервер), `syncLocaleOnLogin` (D-4a слияние).
 * NOT IN SCOPE: precedence-логика (./resolve-locale — чистая), DOM контрола
 *   (lib/nav-lang), редирект корня (app.ts), серверная схема (db/player-locale).
 *
 * Cross-references: ADR-0021 D-3/D-4/D-4a (`docs/decisions/ADR-0021-i18n-global-language/README.md`);
 *   ./resolve-locale (precedence), ./runtime (setLocale каталога), store/game-store
 *   (locale), services/identity (isAuthed), services/api-client (/player/locale).
 */

import type { UiLang } from '@lines98/shared/config/seo';

import { apiClient } from '../../services/api-client.js';
import { isAuthed } from '../../services/identity.js';
import { useGameStore } from '../../store/game-store.js';

import {
  isUiLang,
  readGuestLocale,
  resolveInitialLocale,
  resolveRootRedirectLang,
  writeGuestLocale,
} from './resolve-locale.js';
import { setLocale as setRuntimeLocale } from './runtime.js';

/** Ответ `GET /player/locale` (server-truth сохранённого языка аккаунта). */
interface GetLocaleResponse {
  ok: boolean;
  locale: string | null;
}

/**
 * Имя window-события «lazy-каталог активной локали догрузился, ПРИ ЭТОМ store-locale
 * НЕ менялся». app.ts слушает → перерисовывает активную ИГРОВУЮ фазу на полном каталоге.
 *
 * ЗАЧЕМ: store-locale — единый источник, но lazy-каталог (`t()`) грузится отдельно и
 * АСИНХРОННО. Когда store-locale выставлен ВПЕРЁД каталога (`showLanding` ставит его
 * синхронно + грузит каталог fire-and-forget), часть строк (modeLabel — статичный словарь
 * по store-locale) уже на новом языке, а `t()` ещё падает в en-fallback и ЗАМЕРЗАЕТ —
 * экран показывает СМЕШАННЫЕ языки до ручного переключения (owner-bug 2026-06-28, Архив).
 * Событие = «каталог готов, перерисуй» (аналог `VIEWPORT_APPLIED_EVENT` — единый именованный
 * window-сигнал, НЕ магическая строка по месту вызова).
 */
export const LOCALE_CATALOG_READY_EVENT = 'lines98:locale-catalog-ready';

/**
 * Применить язык как АКТИВНЫЙ: подгрузить его каталог (i18n runtime — чтобы `t()`
 * отдавал новый язык синхронно), затем выставить store-locale (реактивная
 * перерисовка подписчиков БЕЗ перезагрузки — ADR-0021 D-2). `persist` управляет
 * записью выбора: на boot — false (это резолв, не явный выбор человека), на
 * клике в рейле — true (явный выбор → localStorage + при логине сервер).
 */
export async function applyLocale(
  lang: UiLang,
  opts: { persist: boolean } = { persist: false },
): Promise<void> {
  // Каталог нового языка грузится ДО store-set, чтобы перерисовка увидела строки.
  await setRuntimeLocale(lang);
  // store-locale УЖЕ был === lang? Тогда store.setLocale ниже — no-op → подписка app.ts
  // НЕ перерисует активную фазу. НО каталог (выше, через await) только что догрузился: если
  // store-locale выставили ВПЕРЁД каталога (`showLanding` ставит его синхронно + грузит
  // каталог fire-and-forget), к этому моменту `t()` на активном экране мог замёрзнуть на
  // en-fallback рядом с уже-локализованными store-зависимыми строками (modeLabel) =
  // СМЕШАННЫЕ языки (owner-bug 2026-06-28, Архив). Замеряем ДО store.setLocale.
  const storeWasAlready = useGameStore.getState().locale === lang;
  useGameStore.getState().setLocale(lang);
  if (opts.persist) persistLocale(lang);
  // Каталог готов, но store не менялся (no-op set) → подписка молчит. Шлём событие
  // готовности каталога — app.ts перерисует активную ИГРОВУЮ фазу на полном каталоге.
  // Когда store МЕНЯЛСЯ (prev !== lang, напр. выбор языка рейлом) — подписка УЖЕ
  // перерисовала фазу с готовым каталогом (await выше) → событие НЕ шлём, без двойного
  // re-mount.
  if (storeWasAlready && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LOCALE_CATALOG_READY_EVENT));
  }
}

/**
 * Персистнуть ЯВНЫЙ выбор языка (ADR-0021 D-4). Гость → localStorage всегда (мгновенно
 * + переживёт перезаход). Залогиненный → дополнительно сервер (`POST /player/locale`),
 * чтобы язык переезжал между устройствами. localStorage пишем И для залогиненного
 * (мгновенный boot следующей вкладки до ответа сервера). Сервер — fire-and-forget
 * (ошибка/offline не ломает UX; локальный выбор уже применён).
 */
export function persistLocale(lang: UiLang): void {
  writeGuestLocale(lang);
  if (isAuthed()) {
    void apiClient.post('/player/locale', { locale: lang });
  }
}

/**
 * Синхронно разрешить язык первого захода (precedence «явный персист > язык DEEP-LINK
 * URL > язык браузера > en», ADR-0021 D-4 + addendum-001 §B + fix/i18n-unify-homepage)
 * БЕЗ загрузки каталога/смены стора. Нужен на раннем шаге boot для initial-store-locale.
 * `urlLang` — язык deep-link'а (caller добывает из pathname модели B); прямой заход на
 * `/ru/` не должен быть перетёрт языком браузера на en (баг до unify: главная `/ru/`
 * флипалась на en, т.к. bootInitialLocale резолвил браузер). Чистый резолв.
 */
export function resolveBootLocaleSync(urlLang?: string | null): UiLang {
  return resolveInitialLocale({
    explicit: readGuestLocale(),
    urlLang: urlLang ?? null,
    navigatorLanguage:
      typeof navigator !== 'undefined' ? navigator.language : null,
  });
}

/**
 * Синхронно решить, надо ли РЕДИРЕКТНУТЬ голый корень `/` на `/<lang>/` (URL-модель B,
 * ADR-0021 addendum-001 §C.3 / §E): голый `/` ОТДАЁТ английский; редирект на не-en
 * происходит ТОЛЬКО если нет явного персиста И язык браузера = индексируемый не-en
 * (`ru`). Возвращает код языка для редиректа (`ru`) ИЛИ null (остаёмся на en-корне).
 * Чистый sync-резолв (guest localStorage + navigator.language); применяет редирект
 * caller (app.ts replaceState dev / Caddy 302 прод). Серверный `players.locale`
 * подтягивается позже (`syncLocaleOnLogin`) — на самом раннем boot его ещё нет, но
 * это безопасно: если у аккаунта есть язык, sync применит его в сторе после готовности
 * identity (URL корня к тому моменту уже зафиксирован; явный выбор языка → без префикса).
 */
export function resolveRootRedirectSync(): UiLang | null {
  return resolveRootRedirectLang({
    explicit: readGuestLocale(),
    navigatorLanguage:
      typeof navigator !== 'undefined' ? navigator.language : null,
  });
}

/**
 * Разрешить и применить язык ПЕРВОГО захода (boot, ADR-0021 D-4 + addendum-001 §B +
 * fix/i18n-unify-homepage). Precedence «явный персист > язык DEEP-LINK URL > язык
 * браузера > en»: explicit = гостевой localStorage (для залогиненного серверный
 * `players.locale` подтянет `syncLocaleOnLogin`); `urlLang` = язык пути deep-link'а
 * (caller — main.ts из pathname модели B, чтобы прямой `/ru/` не флипнулся на en
 * браузером); сигнал языка по умолчанию = `navigator.language`. Применяет язык (каталог
 * + store) БЕЗ серверной записи. Idempotent при повторе.
 */
export async function bootInitialLocale(urlLang?: string | null): Promise<UiLang> {
  const resolved = resolveBootLocaleSync(urlLang);
  await applyLocale(resolved, { persist: false });
  return resolved;
}

/**
 * Sync-при-логине (ADR-0021 D-4a, «последний явный выбор человека побеждает»).
 * Вызывается при смене identity на АВТОРИЗОВАННУЮ (хук `onIdentityChanged`).
 * Слияние:
 *   - гость уже сделал явный выбор (localStorage) → ОН выигрывает и пишется в
 *     `players.locale` (переносим выбор на аккаунт);
 *   - нет явного гостевого, но у аккаунта есть `players.locale` → берём серверный
 *     и применяем (+ зеркалим в localStorage для мгновенного boot след. вкладки);
 *   - ни там, ни там → ничего не делаем (текущий гео-дефолт остаётся).
 * Best-effort: только для залогиненного; ошибки сети проглатываются (язык косметика).
 */
export async function syncLocaleOnLogin(): Promise<void> {
  if (!isAuthed()) return; // гость — серверного персиста нет (его выбор localStorage)

  const guest = readGuestLocale();
  if (guest) {
    // Гостевой явный выбор побеждает → переносим на аккаунт (D-4a). Применять заново
    // не нужно (boot уже применил гостевой как explicit) — только записать на сервер.
    void apiClient.post('/player/locale', { locale: guest });
    return;
  }

  // Нет гостевого явного выбора → спросить серверный `players.locale`.
  let serverLocale: string | null = null;
  try {
    const res = await apiClient.get<GetLocaleResponse>('/player/locale');
    if (res.ok && res.body.ok) serverLocale = res.body.locale;
  } catch {
    return; // offline / ошибка — оставляем текущий язык (best-effort).
  }

  if (isUiLang(serverLocale)) {
    // Серверный явный выбор есть → применяем и зеркалим в localStorage (мгновенный
    // boot след. вкладки до ответа сервера). НЕ пишем обратно на сервер (он источник).
    await applyLocale(serverLocale, { persist: false });
    writeGuestLocale(serverLocale);
  }
}
